/**
 * FMP Data Service
 *
 * Centralized service for fetching stock data from Financial Modeling Prep (FMP) API.
 * Provides professional-grade metrics for the Stock Research feature.
 *
 * SECURITY: Uses backend proxy (fmp_proxy Cloud Function) to keep API key server-side.
 * This matches the secure pattern used by Agent dashboards (Jerry, Xavier, Kelly, etc.)
 *
 * The proxy endpoint supports all FMP operations via query parameters:
 * - action: The FMP endpoint to call (profile, quote, key-metrics, etc.)
 * - symbol: Stock symbol
 * - limit: Optional limit for paginated endpoints
 * - period: Optional period (annual/quarter)
 *
 * Example: /fmp_proxy?action=profile&symbol=AAPL
 *
 * All calls route through the secure backend proxy to keep API keys server-side.
 */

import { sanitizeSymbol } from '../utils/validation';
import { gatewayFetch } from './gateway';
import { logger } from './loggingService';
import { recordPageFetch } from './pageDataManifest';

// All FMP calls route through the secure backend proxy.
// Legacy direct FMP calls have been removed to prevent API key exposure in client bundle.

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 1000; // Prevent unbounded memory growth
const cache = new Map<string, { data: any; timestamp: number }>();

/** Evict expired entries; if still over limit, drop oldest */
function evictCache() {
  const now = Date.now();
  const keysToDelete: string[] = [];
  cache.forEach((entry, key) => {
    if (now - entry.timestamp >= CACHE_DURATION) keysToDelete.push(key);
  });
  keysToDelete.forEach(k => cache.delete(k));
  if (cache.size > MAX_CACHE_SIZE) {
    const entries: Array<[string, { data: any; timestamp: number }]> = [];
    cache.forEach((v, k) => entries.push([k, v]));
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    const removeCount = cache.size - MAX_CACHE_SIZE;
    for (let i = 0; i < removeCount; i++) cache.delete(entries[i][0]);
  }
}

/**
 * Helper function to fetch from FMP Proxy API with caching
 * Uses backend proxy to keep API key server-side (secure pattern)
 */
async function fetchFMPProxy<T>(
  action: string,
  symbol: string,
  cacheKey: string,
  options?: { limit?: number; period?: string }
): Promise<T | null> {
  // Check cache first
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data as T;
  }

  try {
    const response = await gatewayFetch('fmp', action, {
      auth: 'none',
      params: { symbol, limit: options?.limit || undefined, period: options?.period || undefined },
    });

    // Handle error status codes
    if (response.status === 403) {
      logger.warn(`FMP Proxy 403: ${action} - Endpoint requires higher plan tier`);
      return null;
    }

    if (response.status === 429) {
      logger.warn('FMP Proxy 429: Rate limit exceeded');
      return null;
    }

    if (!response.ok) {
      logger.error(`FMP Proxy error: ${response.status} for ${action}`);
      return null;
    }

    const data = await response.json();

    // Record the (symbol, action) pair — keys only — so a chat question asked
    // on this page can be served from the backend cache this very request just
    // populated (see pageDataManifest.ts).
    recordPageFetch(symbol, action);

    // Check for error response from proxy
    if (data && typeof data === 'object' && 'error' in data) {
      logger.error(`FMP Proxy returned error for ${action}`, { error: data.error });
      return null;
    }

    // Cache the result (evict stale entries if needed)
    if (cache.size >= MAX_CACHE_SIZE) evictCache();
    cache.set(cacheKey, { data, timestamp: Date.now() });

    return data as T;
  } catch (error) {
    logger.error(`FMP Proxy fetch error for ${action}`, { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
}


// ============================================================================
// Type Definitions for FMP API Responses
// ============================================================================

export interface FMPProfile {
  symbol: string;
  companyName: string;
  currency: string;
  exchange: string;
  exchangeShortName?: string;  // May not be present in stable API
  exchangeFullName?: string;   // Stable API returns this instead
  sector: string;
  industry: string;
  description: string;
  ceo: string;
  fullTimeEmployees: string;
  country: string;
  ipoDate: string;
  // Stable API returns marketCap, v3 returns mktCap
  marketCap?: number;
  mktCap?: number;  // Keep for backward compatibility
  price: number;
  change?: number;
  changes?: number;  // Legacy field
  changePercentage?: number;  // Stable API
  changesPercentage?: number; // Legacy field
  range: string; // "52.55 - 198.23"
  beta: number;
  volume?: number;
  averageVolume?: number;
  volAvg?: number;  // Legacy field
  lastDividend?: number;
  lastDiv?: number; // Legacy field
  dcfDiff?: number;
  dcf?: number;
  image: string;
  website: string;
  isEtf: boolean;
  isActivelyTrading: boolean;
}

export interface FMPQuote {
  symbol: string;
  name: string;
  price: number;
  changesPercentage: number;
  change: number;
  dayLow: number;
  dayHigh: number;
  yearHigh: number;
  yearLow: number;
  marketCap: number;
  priceAvg50: number;
  priceAvg200: number;
  exchange: string;
  volume: number;
  avgVolume: number;
  open: number;
  previousClose: number;
  eps: number;
  pe: number;
  earningsAnnouncement: string;
  sharesOutstanding: number;
  timestamp: number;
}

export interface FMPKeyMetrics {
  symbol: string;
  date: string;
  fiscalYear?: string;
  period?: string;
  reportedCurrency?: string;
  revenuePerShare?: number;
  netIncomePerShare?: number;
  operatingCashFlowPerShare?: number;
  freeCashFlowPerShare?: number;
  cashPerShare?: number;
  bookValuePerShare?: number;
  tangibleBookValuePerShare?: number;
  shareholdersEquityPerShare?: number;
  interestDebtPerShare?: number;
  marketCap: number;
  enterpriseValue: number;
  peRatio?: number;  // May not be present in stable API (use priceToEarningsRatio)
  priceToSalesRatio?: number;
  pocfratio?: number;
  pfcfRatio?: number;
  pbRatio?: number;
  ptbRatio?: number;
  evToSales: number;
  enterpriseValueOverEBITDA?: number;  // Stable API uses evToEBITDA
  evToEBITDA?: number;  // Stable API field name
  evToOperatingCashFlow: number;
  evToFreeCashFlow: number;
  earningsYield: number;
  freeCashFlowYield: number;
  debtToEquity?: number;  // May not be in key-metrics, use ratios
  debtToAssets?: number;
  netDebtToEBITDA: number;
  currentRatio: number;
  interestCoverage?: number;
  incomeQuality: number;
  dividendYield?: number;
  payoutRatio?: number;
  salesGeneralAndAdministrativeToRevenue?: number;
  researchAndDevelopementToRevenue?: number;
  intangiblesToTotalAssets?: number;
  capexToOperatingCashFlow: number;
  capexToRevenue?: number;
  capexToDepreciation?: number;
  stockBasedCompensationToRevenue?: number;
  grahamNumber: number;
  roic?: number;  // Legacy field
  returnOnInvestedCapital?: number;  // Stable API field
  returnOnAssets?: number;  // Stable API
  returnOnEquity?: number;  // Stable API
  returnOnCapitalEmployed?: number;  // Stable API
  returnOnTangibleAssets: number;
  grahamNetNet: number;
  workingCapital: number;
  tangibleAssetValue: number;
  netCurrentAssetValue: number;
  investedCapital: number;
  averageReceivables: number;
  averagePayables: number;
  averageInventory: number;
  daysSalesOutstanding?: number;
  daysOfSalesOutstanding?: number;  // Stable API field name
  daysPayablesOutstanding?: number;
  daysOfPayablesOutstanding?: number;  // Stable API field name
  daysOfInventoryOnHand?: number;
  daysOfInventoryOutstanding?: number;  // Stable API field name
  receivablesTurnover: number;
  payablesTurnover: number;
  inventoryTurnover: number;
  roe?: number;  // Legacy field
  capexPerShare?: number;
}

export interface FMPRatios {
  symbol: string;
  date: string;
  fiscalYear?: string;
  period?: string;
  reportedCurrency?: string;
  currentRatio: number;
  quickRatio: number;
  cashRatio: number;
  solvencyRatio?: number;  // Stable API
  daysOfSalesOutstanding: number;
  daysOfInventoryOutstanding: number;
  operatingCycle: number;
  daysOfPayablesOutstanding: number;
  cashConversionCycle: number;
  grossProfitMargin: number;
  ebitMargin?: number;  // Stable API
  ebitdaMargin?: number;  // Stable API
  operatingProfitMargin: number;
  pretaxProfitMargin: number;
  netProfitMargin: number;
  continuousOperationsProfitMargin?: number;  // Stable API
  bottomLineProfitMargin?: number;  // Stable API
  effectiveTaxRate: number;
  returnOnAssets: number;
  returnOnEquity: number;
  returnOnCapitalEmployed: number;
  netIncomePerEBT: number;
  ebtPerEbit: number;
  ebitPerRevenue: number;
  debtRatio?: number;
  debtToAssetsRatio?: number;  // Stable API
  debtEquityRatio?: number;
  debtToEquityRatio?: number;  // Stable API
  debtToCapitalRatio?: number;  // Stable API
  longTermDebtToCapitalization: number;
  longTermDebtToCapitalRatio?: number;  // Stable API
  totalDebtToCapitalization: number;
  interestCoverage?: number;
  interestCoverageRatio?: number;  // Stable API
  financialLeverageRatio?: number;  // Stable API
  workingCapitalTurnoverRatio?: number;  // Stable API
  operatingCashFlowRatio?: number;  // Stable API
  cashFlowToDebtRatio?: number;
  debtServiceCoverageRatio?: number;  // Stable API
  companyEquityMultiplier?: number;
  receivablesTurnover: number;
  payablesTurnover: number;
  inventoryTurnover: number;
  fixedAssetTurnover: number;
  assetTurnover: number;
  operatingCashFlowPerShare: number;
  freeCashFlowPerShare: number;
  cashPerShare: number;
  payoutRatio?: number;
  operatingCashFlowSalesRatio: number;
  freeCashFlowOperatingCashFlowRatio: number;
  cashFlowCoverageRatios?: number;
  operatingCashFlowCoverageRatio?: number;  // Stable API
  shortTermCoverageRatios?: number;
  shortTermOperatingCashFlowCoverageRatio?: number;  // Stable API
  capitalExpenditureCoverageRatio: number;
  dividendPaidAndCapexCoverageRatio: number;
  dividendPayoutRatio: number;
  dividendYield: number;
  dividendYieldPercentage?: number;  // Stable API (already in percentage)
  dividendPerShare?: number;  // Stable API
  revenuePerShare?: number;  // Stable API
  netIncomePerShare?: number;  // Stable API
  bookValuePerShare?: number;  // Stable API
  tangibleBookValuePerShare?: number;  // Stable API
  shareholdersEquityPerShare?: number;  // Stable API
  interestDebtPerShare?: number;  // Stable API
  priceBookValueRatio?: number;
  priceToBookRatio: number;
  priceToSalesRatio: number;
  priceToEarningsRatio?: number;  // Stable API (preferred)
  priceEarningsRatio?: number;  // Legacy
  priceToFreeCashFlowRatio?: number;  // Stable API (preferred)
  priceToFreeCashFlowsRatio?: number;  // Legacy
  priceToOperatingCashFlowRatio?: number;  // Stable API
  priceToOperatingCashFlowsRatio?: number;  // Legacy
  priceCashFlowRatio?: number;
  priceEarningsToGrowthRatio: number;
  forwardPriceToEarningsGrowthRatio?: number;  // Stable API
  priceSalesRatio?: number;
  priceToFairValue?: number;  // Stable API
  priceFairValue?: number;  // Legacy
  debtToMarketCap?: number;  // Stable API
  enterpriseValueMultiple: number;
}

export interface FMPIncomeStatement {
  date: string;
  symbol: string;
  revenue: number;
  costOfRevenue: number;
  grossProfit: number;
  grossProfitRatio: number;
  researchAndDevelopmentExpenses: number;
  sellingGeneralAndAdministrativeExpenses: number;
  operatingExpenses: number;
  costAndExpenses: number;
  operatingIncome: number;
  operatingIncomeRatio: number;
  totalOtherIncomeExpensesNet: number;
  incomeBeforeTax: number;
  incomeBeforeTaxRatio: number;
  incomeTaxExpense: number;
  netIncome: number;
  netIncomeRatio: number;
  eps: number;
  epsdiluted: number;
  weightedAverageShsOut: number;
  weightedAverageShsOutDil: number;
  ebitda: number;
  ebitdaratio: number;
}

export interface FMPBalanceSheet {
  date: string;
  symbol: string;
  totalAssets: number;
  totalCurrentAssets: number;
  cashAndCashEquivalents: number;
  shortTermInvestments: number;
  netReceivables: number;
  inventory: number;
  totalNonCurrentAssets: number;
  propertyPlantEquipmentNet: number;
  goodwill: number;
  intangibleAssets: number;
  totalLiabilities: number;
  totalCurrentLiabilities: number;
  accountPayables: number;
  shortTermDebt: number;
  totalNonCurrentLiabilities: number;
  longTermDebt: number;
  totalStockholdersEquity: number;
  retainedEarnings: number;
  commonStock: number;
}

export interface FMPCashFlowStatement {
  date: string;
  symbol: string;
  netIncome: number;
  depreciationAndAmortization: number;
  stockBasedCompensation: number;
  changeInWorkingCapital: number;
  operatingCashFlow: number;
  capitalExpenditure: number;
  acquisitionsNet: number;
  investmentsInPropertyPlantAndEquipment: number;
  investingActivitiesCashFlow: number;
  debtRepayment: number;
  commonStockRepurchased: number;
  dividendsPaid: number;
  financingActivitiesCashFlow: number;
  freeCashFlow: number;
  netChangeInCash: number;
}

export interface FMPDividendHistory {
  symbol: string;
  historical: Array<{
    date: string;
    label: string;
    adjDividend: number;
    dividend: number;
    recordDate: string;
    paymentDate: string;
    declarationDate: string;
  }>;
}

// ============================================================================
// Data Fetching Functions
// ============================================================================

// ============================================================================
// Stock Research Bundle — all core data in a single HTTP call
// ============================================================================

/**
 * Bundle response from the stock-research-bundle endpoint.
 * Used by the stock research page to fetch all core data in one call.
 */
export interface StockResearchBundle {
  symbol: string;
  profile: FMPProfile[] | FMPProfile | null;
  quote: FMPQuote[] | FMPQuote | null;
  keyMetrics: FMPKeyMetrics[] | null;
  ratios: FMPRatios[] | null;
  incomeStatement: FMPIncomeStatement[] | null;
  balanceSheet: FMPBalanceSheet[] | null;
  cashFlowStatement: FMPCashFlowStatement[] | null;
  /** The dividend figures the research table publishes for this symbol
   *  ([[BUGS #404]] (a)): the regular yield with one-offs split out and
   *  implausible values withheld, and the one-off itself. Null when the
   *  symbol has no published row. */
  dividendSummary?: DividendSummary | null;
}

/**
 * Fetch all core stock research data in a single HTTP call.
 * Returns null if the bundle call fails — caller should fall back to individual fetches.
 *
 * Also populates the in-memory cache for individual endpoints so that
 * expandable modules (which still call individual getters) get cache hits.
 */
export interface DividendSummary {
  /** Regular trailing yield, percent. Null when withheld as implausible. */
  dividendYield: number | null;
  payoutRatio?: number | null;
  pe?: number | null;
  oneOffDividend?: number | null;
  oneOffYield?: number | null;
  oneOffDividendDate?: string | null;
  listingCurrency?: string | null;
}

/** The bundle's dividend summary for `symbol`, from cache only — the stock page
 *  loads the bundle first. Undefined when it was never loaded. */
export function getCachedDividendSummary(symbol: string): DividendSummary | null | undefined {
  const safe = sanitizeSymbol(symbol);
  if (!safe) return undefined;
  const hit = cache.get(`dividendsummary_${safe}`);
  return hit ? (hit.data as DividendSummary | null) : undefined;
}

export async function fetchStockResearchBundle(symbol: string): Promise<StockResearchBundle | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) return null;

  try {
    const response = await gatewayFetch('fmp', 'stock-research-bundle', { auth: 'none', params: { symbol: safeSymbol } });

    if (!response.ok) {
      logger.error(`[FMP] Bundle fetch error: ${response.status}`);
      return null;
    }

    const data = await response.json();
    if (!data || data.error) {
      logger.error('[FMP] Bundle returned error', { error: data?.error });
      return null;
    }

    const bundle = data as StockResearchBundle;

    // Populate individual caches so expandable modules get instant hits
    const now = Date.now();
    const cacheEntries: [string, unknown][] = [
      [`profile_${safeSymbol}`, bundle.profile],
      [`quote_${safeSymbol}`, bundle.quote],
      [`keymetrics_${safeSymbol}_1`, bundle.keyMetrics],
      [`ratios_${safeSymbol}_2`, bundle.ratios],
      [`income_${safeSymbol}_5`, bundle.incomeStatement],
      [`balance_${safeSymbol}_2`, bundle.balanceSheet],
      [`cashflow_${safeSymbol}_2`, bundle.cashFlowStatement],
    ];
    // Cached even when null: "no published row" is an answer, not a miss.
    if ('dividendSummary' in bundle) {
      cache.set(`dividendsummary_${safeSymbol}`, { data: bundle.dividendSummary ?? null, timestamp: now });
    }

    for (const [key, value] of cacheEntries) {
      if (value != null) {
        cache.set(key, { data: value, timestamp: now });
      }
    }

    return bundle;
  } catch (error) {
    logger.error('[FMP] Bundle fetch failed', { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
}


/**
 * Get company profile data
 * Uses secure proxy, falls back to direct FMP calls if disabled
 */
export async function getCompanyProfile(symbol: string): Promise<FMPProfile | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `profile_${safeSymbol}`;
  let data: FMPProfile[] | null;

  data = await fetchFMPProxy<FMPProfile[]>('profile', safeSymbol, cacheKey);

  return data && data.length > 0 ? data[0] : null;
}

/**
 * Get real-time quote data
 * Uses secure proxy
 */
export async function getQuote(symbol: string): Promise<FMPQuote | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `quote_${safeSymbol}`;
  let data: FMPQuote[] | null;

  data = await fetchFMPProxy<FMPQuote[]>('quote', safeSymbol, cacheKey);

  return data && data.length > 0 ? data[0] : null;
}

/**
 * Get key metrics (TTM or specific periods)
 * Uses secure proxy
 */
export async function getKeyMetrics(symbol: string, limit: number = 1): Promise<FMPKeyMetrics[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `keymetrics_${safeSymbol}_${limit}`;

  return fetchFMPProxy<FMPKeyMetrics[]>('key-metrics', safeSymbol, cacheKey, { limit, period: 'annual' });
}

/**
 * Get financial ratios
 * Uses secure proxy
 */
export async function getRatios(symbol: string, limit: number = 1): Promise<FMPRatios[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `ratios_${safeSymbol}_${limit}`;

  return fetchFMPProxy<FMPRatios[]>('ratios', safeSymbol, cacheKey, { limit, period: 'annual' });
}

/**
 * Get income statements
 * Uses secure proxy
 */
export async function getIncomeStatements(symbol: string, limit: number = 5): Promise<FMPIncomeStatement[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `income_${safeSymbol}_${limit}`;

  return fetchFMPProxy<FMPIncomeStatement[]>('income-statement', safeSymbol, cacheKey, { limit, period: 'annual' });
}

/**
 * Get balance sheet statements
 * Uses secure proxy
 */
export async function getBalanceSheets(symbol: string, limit: number = 2): Promise<FMPBalanceSheet[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `balance_${safeSymbol}_${limit}`;

  return fetchFMPProxy<FMPBalanceSheet[]>('balance-sheet-statement', safeSymbol, cacheKey, { limit, period: 'annual' });
}

/**
 * Get cash flow statements
 * Uses secure proxy
 */
export async function getCashFlowStatements(symbol: string, limit: number = 2): Promise<FMPCashFlowStatement[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `cashflow_${safeSymbol}_${limit}`;

  return fetchFMPProxy<FMPCashFlowStatement[]>('cash-flow-statement', safeSymbol, cacheKey, { limit, period: 'annual' });
}

/**
 * Get dividend history
 * Uses secure proxy
 */
export async function getDividendHistory(symbol: string): Promise<FMPDividendHistory | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `dividend_${safeSymbol}`;
  let data: FMPDividendHistory['historical'] | null;

  data = await fetchFMPProxy<FMPDividendHistory['historical']>('dividends', safeSymbol, cacheKey);

  if (data && data.length > 0) {
    return { symbol: safeSymbol, historical: data };
  }
  return null;
}

// ============================================================================
// Analyst & Earnings Data (New Modules)
// ============================================================================

export interface FMPPriceTargetConsensus {
  symbol: string;
  targetHigh: number;
  targetLow: number;
  targetConsensus: number;
  targetMedian: number;
}

export interface FMPAnalystEstimates {
  symbol: string;
  date: string;
  estimatedRevenueAvg: number;
  estimatedRevenueLow: number;
  estimatedRevenueHigh: number;
  estimatedEpsAvg: number;
  estimatedEpsLow: number;
  estimatedEpsHigh: number;
  numberAnalystEstimatedRevenue: number;
  numberAnalystsEstimatedEps: number;
}

export interface FMPEarningsHistory {
  date: string;
  symbol: string;
  eps: number;
  epsEstimated: number;
  time: string;
  revenue: number;
  revenueEstimated: number;
}

export interface FMPUpgradeDowngrade {
  symbol: string;
  publishedDate: string;
  newsURL: string;
  newsTitle: string;
  newsPublisher: string;
  newGrade: string;
  previousGrade: string;
  gradingCompany: string;
  action: string;
  priceWhenPosted: number;
}

export interface FMPStockNews {
  symbol: string;
  publishedDate: string;
  title: string;
  image: string;
  site: string;
  text: string;
  url: string;
}

export interface FMPAnalystRecommendation {
  symbol: string;
  date: string;
  analystRatingsBuy: number;
  analystRatingsHold: number;
  analystRatingsSell: number;
  analystRatingsStrongSell: number;
  analystRatingsStrongBuy: number;
}

/**
 * Get analyst recommendations (Buy/Hold/Sell counts)
 * Uses secure proxy
 */
export async function getAnalystRecommendation(symbol: string): Promise<FMPAnalystRecommendation | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `analyst_recommendation_${safeSymbol}`;
  let data: FMPAnalystRecommendation[] | null;

  data = await fetchFMPProxy<FMPAnalystRecommendation[]>('analyst-recommendation', safeSymbol, cacheKey);

  return data && data.length > 0 ? data[0] : null;
}

/**
 * Get analyst price target consensus
 * Uses secure proxy
 */
export async function getPriceTargetConsensus(symbol: string): Promise<FMPPriceTargetConsensus | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `price_target_consensus_${safeSymbol}`;
  let data: FMPPriceTargetConsensus[] | null;

  data = await fetchFMPProxy<FMPPriceTargetConsensus[]>('price-target-consensus', safeSymbol, cacheKey);

  return data && data.length > 0 ? data[0] : null;
}

/**
 * Get analyst estimates for revenue/EPS
 * Uses secure proxy
 */
export async function getAnalystEstimates(symbol: string, limit: number = 4): Promise<FMPAnalystEstimates[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `analyst_estimates_${safeSymbol}_${limit}`;

  return fetchFMPProxy<FMPAnalystEstimates[]>('analyst-estimates', safeSymbol, cacheKey, { limit, period: 'annual' });
}

/**
 * Get earnings history (actual vs estimated)
 * Uses secure proxy
 */
export async function getEarningsHistory(symbol: string, limit: number = 8): Promise<FMPEarningsHistory[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `earnings_history_${safeSymbol}_${limit}`;

  return fetchFMPProxy<FMPEarningsHistory[]>('earnings', safeSymbol, cacheKey);
}

/**
 * Get analyst upgrades/downgrades (stock grades)
 * Uses secure proxy
 */
export async function getUpgradeDowngrades(symbol: string, limit: number = 10): Promise<FMPUpgradeDowngrade[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `upgrades_downgrades_${safeSymbol}_${limit}`;

  return fetchFMPProxy<FMPUpgradeDowngrade[]>('grades', safeSymbol, cacheKey);
}

/**
 * Get stock news for a symbol
 * Uses secure proxy
 * Note: Uses the newer /news/stock endpoint with symbols parameter
 */
export async function getStockNews(symbol: string, limit: number = 10): Promise<FMPStockNews[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `stock_news_${safeSymbol}_${limit}`;
  let data: FMPStockNews[] | null;

  data = await fetchFMPProxy<FMPStockNews[]>('news-stock', safeSymbol, cacheKey);

  // Limit results client-side since API may not support limit parameter
  return data ? data.slice(0, limit) : null;
}

/**
 * Get press releases for a symbol
 * Uses secure proxy
 */
export async function getPressReleases(symbol: string, limit: number = 5): Promise<FMPStockNews[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `press_releases_${safeSymbol}_${limit}`;
  let data: FMPStockNews[] | null;

  data = await fetchFMPProxy<FMPStockNews[]>('news-press-releases', safeSymbol, cacheKey);

  return data ? data.slice(0, limit) : null;
}

// ============================================================================
// Financial Scores (Pre-calculated Z-Score & F-Score)
// ============================================================================

export interface FMPFinancialScores {
  symbol: string;
  altmanZScore: number;
  piotroskiScore: number;
  workingCapital: number;
  totalAssets: number;
  retainedEarnings: number;
  ebit: number;
  totalLiabilities: number;
  revenue: number;
  marketCap: number;
}

/**
 * Get pre-calculated financial scores (Altman Z-Score & Piotroski F-Score)
 * Uses secure proxy
 *
 * This endpoint provides pre-calculated scores, eliminating the need for
 * manual calculation from multiple financial statements.
 */
export async function getFinancialScores(symbol: string): Promise<FMPFinancialScores | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `financial_scores_${safeSymbol}`;
  let data: FMPFinancialScores[] | null;

  data = await fetchFMPProxy<FMPFinancialScores[]>('financial-scores', safeSymbol, cacheKey);

  return data && data.length > 0 ? data[0] : null;
}

// ============================================================================
// Net Tangible Assets (NTA) Calculation
// ============================================================================

/**
 * NTA (Net Tangible Assets) calculation result from backend
 * Used for value investing analysis (Graham/Buffett style)
 */
export interface NTACalculationResult {
  symbol: string;
  nta: number | null;                        // Net Tangible Assets (absolute value)
  nta_per_share: number | null;              // NTA per share
  price_to_nta: number | null;               // Price-to-NTA ratio (P/NTA)
  tangible_book_value_per_share: number | null;  // From FMP key-metrics if available
  book_value_per_share: number | null;       // From FMP key-metrics
  calculation_details: {
    extracted_fields?: {
      total_assets: number | null;
      total_liabilities: number | null;
      goodwill: number | null;
      intangible_assets: number | null;
      goodwill_and_intangibles: number | null;
      total_stockholders_equity: number | null;
      shares_outstanding: number | null;
    };
    nta?: {
      calculation_method: string;
      total_intangibles: number;
      error?: string;
      warning?: string;
    };
    price_to_nta?: {
      interpretation: string;
    };
  };
  data_quality: 'complete' | 'partial' | 'missing' | 'insufficient';
  warnings: string[];
  error?: string;
}

/**
 * Get NTA (Net Tangible Assets) calculation for a stock
 * Uses secure proxy to calculate NTA from balance sheet, key metrics, and quote data
 *
 * NTA = Total Assets - Intangibles (Goodwill + Intangible Assets) - Total Liabilities
 *
 * This is a key value investing metric:
 * - P/NTA < 1.0: Trading below tangible book value (potential deep value)
 * - P/NTA 1.0-2.0: Trading near tangible book value
 * - P/NTA > 2.0: Trading at premium to tangible book
 */
export async function getNTACalculation(symbol: string): Promise<NTACalculationResult | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `nta_calculation_${safeSymbol}`;

  // Check cache first
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data as NTACalculationResult;
  }

  try {
    const response = await gatewayFetch('fmp', 'nta-calculation', { auth: 'none', params: { symbol: safeSymbol } });

    if (!response.ok) {
      logger.warn(`[FMP] NTA calculation request failed: ${response.status}`);
      return null;
    }

    const data: NTACalculationResult = await response.json();

    // Cache successful results
    if (data && !data.error) {
      cache.set(cacheKey, { data, timestamp: Date.now() });
    }

    return data;
  } catch (error) {
    logger.error('[FMP] Error fetching NTA calculation', { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
}

// ============================================================================
// Stock Peers / Competitor Data
// ============================================================================

/**
 * Stock peers response from FMP API or LLM-powered endpoint
 * Returns a list of similar companies in the same industry
 */
export interface FMPStockPeers {
  symbol: string;
  peersList: string[];
  source?: 'fmp' | 'ai' | 'none';  // Source indicator for data provenance
}

/**
 * Get stock peers/competitors for a given symbol
 * Uses LLM-powered endpoint (PRIMARY) via secure proxy
 *
 * Returns a list of similar companies in the same industry
 * The 'source' field indicates whether data came from AI or FMP
 */
export async function getStockPeers(symbol: string): Promise<FMPStockPeers | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `stock_peers_llm_${safeSymbol}`;

  try {
    const response = await gatewayFetch('fmp', 'stock-peers-llm', { auth: 'none', params: { symbol: safeSymbol } });

    if (!response.ok) {
      logger.warn(`[FMP] LLM peers request failed: ${response.status}`);
      return null;
    }

    const data: FMPStockPeers = await response.json();

    // Cache the result
    if (data && data.peersList && data.peersList.length > 0) {
      cache.set(cacheKey, { data, timestamp: Date.now() });
    }

    return data;
  } catch (error) {
    logger.error('[FMP] Error fetching LLM-powered peers', { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
}

/**
 * LLM Analyst Consensus Response
 * Used when FMP analyst-recommendation returns no data
 */
export interface LLMAnalystConsensus {
  symbol: string;
  consensus: 'Strong Buy' | 'Buy' | 'Hold' | 'Sell' | 'Strong Sell' | null;
  confidence: 'high' | 'medium' | 'low' | null;
  rationale: string;
  targetPrice?: number | null;
  source: 'ai' | null;
}

// ============================================================================
// ETF Data Types (FMP Ultimate Plan)
// ============================================================================

/**
 * ETF Holding - Individual position within an ETF
 */
export interface ETFHolding {
  asset: string;        // Stock symbol (e.g., "AAPL")
  name: string;         // Company name
  weight: number;       // Percentage weight (0-100)
  shares: number;       // Number of shares held
  marketValue?: number; // Market value of holding
}

/**
 * ETF Information - Metadata about the ETF
 */
export interface ETFInfo {
  symbol: string;           // ETF symbol (e.g., "SPY")
  name: string;             // ETF name
  expenseRatio: number;     // Annual expense ratio (percentage)
  aum: number;              // Assets Under Management (USD)
  inceptionDate: string;    // Launch date (ISO format)
  issuer: string;           // Fund issuer (e.g., "Vanguard", "BlackRock")
  domicile?: string;        // Country of domicile
  etfCompany?: string;      // ETF company/provider
}

/**
 * ETF Sector Weight - Sector allocation within an ETF
 */
export interface ETFSectorWeight {
  sector: string;       // Sector name (e.g., "Technology", "Healthcare")
  weight: number;       // Percentage weight (0-100)
}

/**
 * Index Constituent - Stock within an index (e.g., ASX 200, S&P 500)
 */
export interface IndexConstituent {
  symbol: string;       // Stock symbol
  name: string;         // Company name
  sector: string;       // GICS sector
  subSector?: string;   // GICS sub-sector
  weight?: number;      // Index weight percentage (if available)
}

/**
 * Get analyst consensus via LLM when FMP has no data.
 * This endpoint uses Gemini to analyze financial metrics and provide a consensus rating.
 * ONLY call this when getAnalystRecommendation returns null.
 */
export async function getAnalystConsensusLLM(symbol: string): Promise<LLMAnalystConsensus | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `analyst_consensus_llm_${safeSymbol}`;

  // Check cache first
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  try {
    const response = await gatewayFetch('fmp', 'analyst-consensus-llm', { auth: 'none', params: { symbol: safeSymbol } });

    if (!response.ok) {
      logger.warn(`[FMP] LLM consensus request failed: ${response.status}`);
      return null;
    }

    const data: LLMAnalystConsensus = await response.json();

    // Cache the result if we got a consensus
    if (data && data.consensus) {
      cache.set(cacheKey, { data, timestamp: Date.now() });
    }

    return data;
  } catch (error) {
    logger.error('[FMP] Error fetching LLM analyst consensus', { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
}

// ============================================================================
// Professional Metrics Calculations
// ============================================================================

/**
 * Calculate Altman Z-Score for bankruptcy prediction
 *
 * Z = 1.2*X1 + 1.4*X2 + 3.3*X3 + 0.6*X4 + 1.0*X5
 * Where:
 * X1 = Working Capital / Total Assets
 * X2 = Retained Earnings / Total Assets
 * X3 = EBIT / Total Assets
 * X4 = Market Value of Equity / Total Liabilities
 * X5 = Sales / Total Assets
 *
 * Interpretation:
 * Z > 2.99: "Safe" zone
 * 1.81 < Z < 2.99: "Grey" zone
 * Z < 1.81: "Distress" zone
 */
export function calculateAltmanZScore(
  balanceSheet: FMPBalanceSheet,
  incomeStatement: FMPIncomeStatement,
  marketCap: number
): { score: number | null; rating: 'Safe' | 'Grey' | 'Distress' | null } {
  try {
    const totalAssets = balanceSheet.totalAssets;
    if (!totalAssets || totalAssets <= 0) {
      return { score: null, rating: null };
    }

    const workingCapital = balanceSheet.totalCurrentAssets - balanceSheet.totalCurrentLiabilities;
    const retainedEarnings = balanceSheet.retainedEarnings || 0;
    const ebit = incomeStatement.operatingIncome || 0;
    const totalLiabilities = balanceSheet.totalLiabilities || 1; // Avoid division by zero
    const revenue = incomeStatement.revenue || 0;

    // Calculate components
    const x1 = workingCapital / totalAssets;
    const x2 = retainedEarnings / totalAssets;
    const x3 = ebit / totalAssets;
    const x4 = marketCap / totalLiabilities;
    const x5 = revenue / totalAssets;

    // Altman Z-Score formula
    const zScore = (1.2 * x1) + (1.4 * x2) + (3.3 * x3) + (0.6 * x4) + (1.0 * x5);

    // Determine rating
    let rating: 'Safe' | 'Grey' | 'Distress';
    if (zScore > 2.99) {
      rating = 'Safe';
    } else if (zScore > 1.81) {
      rating = 'Grey';
    } else {
      rating = 'Distress';
    }

    return { score: Math.round(zScore * 100) / 100, rating };
  } catch (error) {
    logger.error('Error calculating Altman Z-Score', { error: error instanceof Error ? error.message : String(error) });
    return { score: null, rating: null };
  }
}

/**
 * Calculate Piotroski F-Score for financial strength
 *
 * Score of 0-9 based on 9 binary criteria:
 *
 * Profitability (4 points):
 * 1. Positive net income (ROA > 0)
 * 2. Positive operating cash flow
 * 3. Improving ROA (ROA_current > ROA_previous)
 * 4. Quality of earnings (Operating CF > Net Income)
 *
 * Leverage/Liquidity (3 points):
 * 5. Decreasing long-term debt ratio
 * 6. Improving current ratio
 * 7. No new equity issued
 *
 * Operating Efficiency (2 points):
 * 8. Improving gross margin
 * 9. Improving asset turnover
 *
 * Interpretation:
 * 8-9: "Strong" financial health
 * 5-7: "Moderate" financial health
 * 0-4: "Weak" financial health
 */
export function calculatePiotroskiFScore(
  currentRatios: FMPRatios,
  previousRatios: FMPRatios | null,
  currentBalanceSheet: FMPBalanceSheet,
  previousBalanceSheet: FMPBalanceSheet | null,
  currentCashFlow: FMPCashFlowStatement,
  currentIncome: FMPIncomeStatement
): { score: number; rating: 'Strong' | 'Moderate' | 'Weak' } {
  let score = 0;

  // Profitability (4 points)
  // 1. Positive ROA
  if (currentRatios.returnOnAssets > 0) score += 1;

  // 2. Positive operating cash flow
  if (currentCashFlow.operatingCashFlow > 0) score += 1;

  // 3. Improving ROA
  if (previousRatios && currentRatios.returnOnAssets > previousRatios.returnOnAssets) score += 1;

  // 4. Quality of earnings (OCF > Net Income)
  if (currentCashFlow.operatingCashFlow > currentIncome.netIncome) score += 1;

  // Leverage/Liquidity (3 points)
  // 5. Decreasing long-term debt ratio
  if (previousBalanceSheet && currentBalanceSheet.longTermDebt < previousBalanceSheet.longTermDebt) score += 1;

  // 6. Improving current ratio
  if (previousRatios && currentRatios.currentRatio > previousRatios.currentRatio) score += 1;

  // 7. No new shares issued (shares outstanding same or less)
  if (previousBalanceSheet) {
    const currentShares = currentBalanceSheet.commonStock || 0;
    const previousShares = previousBalanceSheet.commonStock || 0;
    if (currentShares <= previousShares) score += 1;
  }

  // Operating Efficiency (2 points)
  // 8. Improving gross margin
  if (previousRatios && currentRatios.grossProfitMargin > previousRatios.grossProfitMargin) score += 1;

  // 9. Improving asset turnover
  if (previousRatios && currentRatios.assetTurnover > previousRatios.assetTurnover) score += 1;

  // Determine rating
  let rating: 'Strong' | 'Moderate' | 'Weak';
  if (score >= 8) {
    rating = 'Strong';
  } else if (score >= 5) {
    rating = 'Moderate';
  } else {
    rating = 'Weak';
  }

  return { score, rating };
}

/**
 * Determine market cap tier
 */
export function getMarketCapTier(marketCap: number): 'Mega' | 'Large' | 'Mid' | 'Small' | 'Micro' {
  if (marketCap >= 200e9) return 'Mega';      // $200B+
  if (marketCap >= 10e9) return 'Large';      // $10B - $200B
  if (marketCap >= 2e9) return 'Mid';         // $2B - $10B
  if (marketCap >= 300e6) return 'Small';     // $300M - $2B
  return 'Micro';                              // < $300M
}

/**
 * Calculate growth rate between two values
 */
export function calculateGrowthRate(current: number, previous: number): number | null {
  if (!previous || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/**
 * Calculate CAGR (Compound Annual Growth Rate)
 */
export function calculateCAGR(startValue: number, endValue: number, years: number): number | null {
  if (!startValue || startValue <= 0 || !endValue || years <= 0) return null;
  return (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
}

/**
 * Count consecutive years of dividend increases
 */
export function countDividendStreak(history: FMPDividendHistory['historical']): number {
  if (!history || history.length < 2) return 0;

  // Group dividends by year and sum them
  const yearlyDividends = new Map<number, number>();
  history.forEach(div => {
    const year = new Date(div.date).getFullYear();
    yearlyDividends.set(year, (yearlyDividends.get(year) || 0) + div.dividend);
  });

  // Sort years descending
  const years = Array.from(yearlyDividends.keys()).sort((a, b) => b - a);

  let streak = 0;
  for (let i = 0; i < years.length - 1; i++) {
    const currentYearDiv = yearlyDividends.get(years[i]) || 0;
    const previousYearDiv = yearlyDividends.get(years[i + 1]) || 0;

    if (currentYearDiv > previousYearDiv) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
}

// ============================================================================
// ETF Data Fetch Functions (FMP Ultimate Plan)
// ============================================================================

/**
 * Get ETF holdings - top positions and weights
 */
export async function getETFHoldings(symbol: string): Promise<ETFHolding[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `etf_holdings_${safeSymbol}`;

  return fetchFMPProxy<ETFHolding[]>('etf-holdings', safeSymbol, cacheKey);
}

/**
 * Get ETF information - expense ratio, AUM, inception date
 */
export async function getETFInfo(symbol: string): Promise<ETFInfo | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `etf_info_${safeSymbol}`;

  const data = await fetchFMPProxy<ETFInfo[]>('etf-info', safeSymbol, cacheKey);
  return data && data.length > 0 ? data[0] : null;
}

/**
 * Get ETF sector weights - sector allocation breakdown
 */
export async function getETFSectorWeights(symbol: string): Promise<ETFSectorWeight[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `etf_sector_weights_${safeSymbol}`;

  return fetchFMPProxy<ETFSectorWeight[]>('etf-sector-weights', safeSymbol, cacheKey);
}

/**
 * Get index constituents - stocks within an index (e.g., ASX 200, S&P 500)
 * @param index Index symbol (e.g., '^AXJO' for ASX 200, '^GSPC' for S&P 500)
 */
export async function getIndexConstituents(index: string): Promise<IndexConstituent[] | null> {
  const safeIndex = sanitizeSymbol(index);
  if (!safeIndex) {
    logger.warn(`[FMP] Invalid index symbol rejected: ${index}`);
    return null;
  }

  const cacheKey = `index_constituents_${safeIndex}`;

  return fetchFMPProxy<IndexConstituent[]>('index-constituents', safeIndex, cacheKey);
}

// ============================================
// ETF endpoints (Part 2 — ETF research layout)
// ============================================
//
// FMP endpoints already exist in functions/utils/market_data/_config.py with
// 12-24h backend cache TTL:
//   'etf-info'           → /v4/etf-info             expense ratio, AUM, inception
//   'etf-holdings'       → /v3/etf-holder           top holdings list
//   'etf-sector-weights' → /v3/etf-sector-weightings sector breakdown
// We wrap them here with cache-aware proxy + sanitize-on-input.

export interface FMPEtfInfo {
  symbol: string;
  name: string;
  description?: string;
  isin?: string;
  cusip?: string;
  assetClass?: string;
  aum?: number;
  expenseRatio?: number;
  avgVolume?: number;
  inceptionDate?: string;
  domicile?: string;
  website?: string;
  sectorsList?: Array<{ industry: string; exposure: number }>;
  // Issuer is often in 'etfCompany' or 'family' depending on plan version.
  etfCompany?: string;
  family?: string;
}

export interface FMPEtfHolding {
  asset: string;            // ticker symbol (or CUSIP for some bond holdings)
  name?: string;
  weightPercentage: number; // 0-100 scale from FMP
  isin?: string;
  cusip?: string;
  sharesNumber?: number;
  marketValue?: number;
  // Some FMP responses include sector on holdings, others don't
  sector?: string;
  industry?: string;
}

export interface FMPEtfSectorWeight {
  sector: string;
  weightPercentage: string;  // FMP returns this as a string like "32.45%"
}

export async function getEtfInfo(symbol: string): Promise<FMPEtfInfo | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) return null;
  const data = await fetchFMPProxy<FMPEtfInfo[] | FMPEtfInfo>(
    'etf-info', safeSymbol, `etf_info_${safeSymbol}`,
  );
  if (!data) return null;
  return Array.isArray(data) ? (data[0] ?? null) : data;
}

export async function getEtfHoldings(symbol: string): Promise<FMPEtfHolding[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) return null;
  return fetchFMPProxy<FMPEtfHolding[]>(
    'etf-holdings', safeSymbol, `etf_holdings_${safeSymbol}`,
  );
}

export async function getEtfSectorWeights(symbol: string): Promise<FMPEtfSectorWeight[] | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) return null;
  return fetchFMPProxy<FMPEtfSectorWeight[]>(
    'etf-sector-weights', safeSymbol, `etf_sectors_${safeSymbol}`,
  );
}

/**
 * Snapshot of an ETF as captured by the daily backend scanner
 * (`functions/api/scanners/etf/_row.py:DiscoveryETFRow`). Mirrors that
 * dataclass's field names exactly so a contract test can pin them.
 *
 * Use this in preference to direct FMP calls — the snapshot is populated
 * by the prod-tier scanner regardless of the caller's FMP plan, so it
 * survives the 403 wall lower-tier accounts hit on /v3/etf-holder,
 * /v4/etf-info, /v3/etf-sector-weightings.
 */
export interface ETFSnapshot {
  symbol: string;
  name: string;
  exchange: string;
  category: string;
  issuer: string;
  inceptionYear: number;
  expenseRatio: number;
  aum: number;
  avgVolume90d: number;
  totalReturn1y: number;
  totalReturn3y: number;
  totalReturn5y?: number | null;
  trackingError1y: number;
  premiumDiscount: number;
  distributionYield: number;
  /** Null when there was too little overlapping history to regress — never
   *  defaulted to 1.0, which is an ordinary reading (BUGS #78). */
  beta3y: number | null;
  /** Share of the fund's movement the market explains, 0-1. */
  rSquared3y?: number | null;
  volatility1y: number;
  maxDrawdown3y: number;
  sharpeRatio3y: number | null;
  topHoldingsPct: number;
  lastUpdatedAt?: string | null;
  dataSource?: string;
  // Extended fields (populated by scanner phase 2 — backward-compat: may
  // be missing on snapshots written before the schema extension).
  topHoldings?: Array<{ asset: string; name?: string; weightPercentage: number }>;
  sectorBreakdown?: Array<{ sector: string; weightPercentage: number }>;
  /** Peer symbols the daily scan derived from the whole ETF population.
   *  `peerBasis` says how: 'holdings_overlap' is measured similarity,
   *  'category' is a weaker fallback for funds outside the holdings tier. */
  peers?: string[];
  peerBasis?: 'holdings_overlap' | 'category' | '';
}

/**
 * Read the cached ETF snapshot for a symbol. Returns null on cache miss
 * (scanner hasn't run for this symbol yet), letting callers fall back to
 * live FMP if they have plan-tier access.
 */
export async function getEtfSnapshot(symbol: string): Promise<ETFSnapshot | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) return null;
  try {
    const resp = await gatewayFetch('portfolio', 'etf_snapshot', { auth: 'none', params: { symbol: safeSymbol } });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (!data || data.snapshot == null) return null;
    return data.snapshot as ETFSnapshot;
  } catch {
    return null;
  }
}

/**
 * Get alpha signals for a stock symbol
 */
export async function getAlphaSignals(symbol: string): Promise<import('../types/research').AlphaSignalsData | null> {
  const safeSymbol = sanitizeSymbol(symbol);
  if (!safeSymbol) {
    logger.warn(`[FMP] Invalid symbol rejected: ${symbol}`);
    return null;
  }

  const cacheKey = `alpha_signals_${safeSymbol}`;

  return fetchFMPProxy<import('../types/research').AlphaSignalsData>('alpha-signals', safeSymbol, cacheKey);
}

/**
 * Clear the cache (useful for forcing refresh)
 */
export function clearCache(): void {
  cache.clear();
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { size: number; entries: string[] } {
  return {
    size: cache.size,
    entries: Array.from(cache.keys())
  };
}
